import { forwardRef, type ComponentPropsWithoutRef, type ComponentRef } from "react";

import { Icon } from "../../atoms/icons";
import {
  getTextFieldVisualState,
  textFieldContainerVariants,
  textFieldIconClass,
  textFieldIconSizeMap,
  textFieldInputClass,
  textFieldPlaceholderColor,
} from "../../atoms/text-field";
import { Typography } from "../../atoms/typography";
import { cn } from "../../lib";
import { Pressable } from "../../primitives";
import type { DatePickerTriggerProps } from "./types";

const FIELD_SIZE = "sm";

type TriggerProps = DatePickerTriggerProps &
  Omit<ComponentPropsWithoutRef<typeof Pressable>, keyof DatePickerTriggerProps>;

export const DatePickerTrigger = forwardRef<ComponentRef<typeof Pressable>, TriggerProps>(
  (
    {
      label,
      placeholder,
      hasValue,
      leadingIcon = "CalendarDots",
      disabled,
      accessibilityLabel,
      className,
      ...props
    },
    ref,
  ) => {
    const visualState = getTextFieldVisualState({
      disabled: Boolean(disabled),
      isFocused: false,
      state: "default",
    });
    const iconSize = textFieldIconSizeMap[FIELD_SIZE];
    const iconClass = textFieldIconClass[visualState];

    return (
      <Pressable
        ref={ref}
        disabled={disabled}
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ disabled: Boolean(disabled) }}
        className={cn(
          textFieldContainerVariants({ size: FIELD_SIZE, visualState }),
          "w-full border-solid outline-none",
          "web:focus-visible:border-luce-brand-primaryBorder",
          className,
        )}
        {...props}
      >
        {leadingIcon ? (
          <Icon
            name={leadingIcon}
            size={iconSize}
            weight="regular"
            className={cn("shrink-0", iconClass)}
          />
        ) : null}
        <Typography
          variant="body-md"
          numberOfLines={1}
          // The placeholder is a label here, not an input's placeholder
          color={hasValue ? undefined : textFieldPlaceholderColor[visualState]}
          className={cn("flex-1 text-left", hasValue && textFieldInputClass[visualState])}
        >
          {hasValue ? label : placeholder}
        </Typography>
        <Icon
          name="CaretDown"
          size={iconSize}
          weight="regular"
          className={cn("shrink-0", iconClass)}
        />
      </Pressable>
    );
  },
);

DatePickerTrigger.displayName = "DatePickerTrigger";
